// ===========================================
// New Conversation Modal Component
// ===========================================
// Modal for starting a new conversation with a user

import { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  Typography,
  Box,
  Avatar,
  Chip,
  CircularProgress,
  IconButton,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { Recipient, OFFICE_GROUP_LABEL, getRoleColor, messagingLabel, recipientSubtitle } from '../../api/messages.api';

// ===========================================
// TYPES
// ===========================================

interface NewConversationModalProps {
  open: boolean;
  recipients: Recipient[];
  loading?: boolean;
  recipientsLoading?: boolean;
  onClose: () => void;
  onSend: (recipientId: string, content: string) => void;
}

// ===========================================
// HELPER FUNCTIONS
// ===========================================

function getInitials(firstName: string, lastName: string): string {
  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
}

/** The office (Admins and staff who handle messages) is one group, whatever each login's role. */
export { OFFICE_GROUP_LABEL };

/** The picker's group: the office together, everyone else under their label ("Teacher", "Support", …). */
function recipientGroup(r: Recipient): string {
  return r.isOffice ? OFFICE_GROUP_LABEL : messagingLabel(r);
}

/** Group order: the office first, then these; an unlisted label (an owner title) after the office. */
const GROUP_ORDER = [OFFICE_GROUP_LABEL, 'Support', 'Teacher', 'Parent', 'Student'];
const groupRank = (group: string) => Math.max(GROUP_ORDER.indexOf(group), 0.5);

function recipientColor(r: Recipient) {
  return getRoleColor(r.isOffice ? 'ADMIN' : r.role);
}

// ===========================================
// COMPONENT
// ===========================================

export default function NewConversationModal({
  open,
  recipients,
  loading = false,
  recipientsLoading = false,
  onClose,
  onSend,
}: NewConversationModalProps) {
  const [selectedRecipient, setSelectedRecipient] = useState<Recipient | null>(null);
  const [content, setContent] = useState('');

  // Group recipients: the office first, then by role
  const groupedRecipients = useMemo(() => {
    return [...recipients].sort((a, b) => {
      // Sort by group first (groupBy needs each group contiguous), then by name
      const ga = recipientGroup(a);
      const gb = recipientGroup(b);
      const groupCompare = groupRank(ga) - groupRank(gb) || ga.localeCompare(gb);
      if (groupCompare !== 0) return groupCompare;
      return `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`);
    });
  }, [recipients]);

  const canSend = selectedRecipient && content.trim().length > 0 && content.length <= 5000;

  const handleSend = () => {
    if (!canSend) return;
    onSend(selectedRecipient!.id, content.trim());
    handleClose();
  };

  const handleClose = () => {
    setSelectedRecipient(null);
    setContent('');
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          New Message
          <IconButton
            size="small"
            onClick={handleClose}
            disabled={loading}
            sx={{ ml: 2 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {/* Recipient selector */}
        <Autocomplete
          options={groupedRecipients}
          value={selectedRecipient}
          onChange={(_, newValue) => setSelectedRecipient(newValue)}
          loading={recipientsLoading}
          getOptionLabel={(option) => `${option.firstName} ${option.lastName}`}
          groupBy={recipientGroup}
          renderOption={(props, option) => (
            <Box component="li" {...props}>
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  mr: 1.5,
                  bgcolor: `${recipientColor(option)}.main`,
                  fontSize: '0.875rem',
                }}
              >
                {getInitials(option.firstName, option.lastName)}
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2">
                  {option.firstName} {option.lastName}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {recipientSubtitle(option)}
                </Typography>
              </Box>
              <Chip
                label={messagingLabel(option)}
                size="small"
                color={recipientColor(option)}
                sx={{ height: 20, fontSize: '0.7rem' }}
              />
            </Box>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="To"
              placeholder="Select a recipient..."
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {recipientsLoading ? <CircularProgress size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          sx={{ mb: 2 }}
        />

        {/* Message input */}
        <TextField
          fullWidth
          multiline
          rows={4}
          label="Message"
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder="Type your message..."
          disabled={loading}
          error={content.length > 5000}
          helperText={
            content.length > 4500
              ? `${5000 - content.length} characters remaining`
              : ''
          }
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSend}
          disabled={!canSend || loading}
          startIcon={loading ? <CircularProgress size={16} /> : undefined}
        >
          Send Message
        </Button>
      </DialogActions>
    </Dialog>
  );
}
